import { getTypeFromAbbreviation } from '../constants/kpiTimeTypes';

/**
 * Generate a CSV template for KPI import
 */
export const generateKPIImportTemplate = (): string => {
  const headers = ['Category Name', 'KPI Name', 'KPI Code', 'Measurement Type', 'Description', 'Type'];
  
  const sampleData = [
    ['Financial Performance', 'Revenue', 'REV_001', 'Value', 'Monthly revenue figures', 'MTD'],
    ['Financial Performance', 'YTD Revenue', 'REV_YTD_001', 'Value', 'Cumulative revenue from fiscal year start', 'YTD'],
    ['Financial Performance', 'Rolling 12M Revenue', 'REV_R12_001', 'Value', 'Revenue over the last 12 months', 'R12'],
    ['Financial Performance', 'Rolling 3M Revenue', 'REV_R3_001', 'Value', 'Revenue over the last 3 months', 'R3'],
    ['Customer Service', 'Customer Satisfaction', 'CSAT_001', 'Percentage', 'Customer satisfaction survey results', 'MTD'],
    ['Customer Service', 'YTD Satisfaction', 'CSAT_YTD_001', 'Percentage', 'Year to date satisfaction average', 'YTD'],
    ['Operations', 'Efficiency Rate', 'EFF_001', 'Percentage', 'Operational efficiency percentage', 'MTD'],
    ['Operations', 'QTD Efficiency', 'EFF_QTD_001', 'Percentage', 'Efficiency from quarter start', 'QTD'],
    ['Operations', 'Rolling 6M Efficiency', 'EFF_R6_001', 'Percentage', 'Efficiency over 6 months', 'R6'],
    ['Human Resources', 'Employee Satisfaction', 'EMP_SAT_001', 'Percentage', 'Employee satisfaction survey results', 'MTD'],
    ['Human Resources', 'Prior Year Retention', 'RETAIN_PY_001', 'Percentage', 'Retention rate from last year', 'PY'],
    ['Human Resources', 'Prior Month Turnover', 'TURN_PM_001', 'Percentage', 'Turnover from last month', 'PM']
  ];

  // Create CSV content with proper escaping
  const csvContent = [headers, ...sampleData]
    .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');

  return csvContent;
};

/**
 * Download a CSV file with the given content
 */
export const downloadCSVFile = (content: string, filename: string): void => {
  // Add BOM for proper UTF-8 encoding in Excel
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + content], { 
    type: 'text/csv;charset=utf-8;' 
  });
  
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  }
};

/**
 * Get the current date in YYYY-MM-DD format for filename
 */
export const getCurrentDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Generate and download KPI import template with current date
 */
export const downloadKPIImportTemplate = (): void => {
  const csvContent = generateKPIImportTemplate();
  const dateString = getCurrentDateString();
  const filename = `kpi-import-template-${dateString}.csv`;
  downloadCSVFile(csvContent, filename);
};

/**
 * Validate measurement types against allowed values
 */
export const VALID_MEASUREMENT_TYPES = ['Value', 'Units', 'Percentage', 'Hours'];

/**
 * Parse KPI import data from CSV format
 */
export interface ParsedKPIRow {
  categoryName: string;
  kpiName: string;
  kpiCode: string;
  measurementType: string;
  description?: string;
  type: string;
  rowNumber: number;
}

export const parseKPIImportData = (csvData: string[][]): ParsedKPIRow[] => {
  if (!csvData || csvData.length < 2) {
    throw new Error('CSV file must contain headers and at least one data row');
  }

  const headers = csvData[0];
  const dataRows = csvData.slice(1);
  
  // Debug logging
  console.log('Headers received:', headers);
  console.log('Data rows count:', dataRows.length);

  // Find header indices with exact matches first, then partial matches
  const categoryNameIndex = headers.findIndex(h => 
    h.toLowerCase() === 'category name' || h.toLowerCase().includes('category')
  );
  const kpiNameIndex = headers.findIndex(h => 
    h.toLowerCase() === 'kpi name' || h.toLowerCase().includes('kpi name')
  );
  const kpiCodeIndex = headers.findIndex(h => 
    h.toLowerCase() === 'kpi code' || h.toLowerCase().includes('kpi code')
  );
  const measurementTypeIndex = headers.findIndex(h => 
    h.toLowerCase() === 'measurement type' || h.toLowerCase().includes('measurement')
  );
  const descriptionIndex = headers.findIndex(h => 
    h.toLowerCase() === 'description' || h.toLowerCase().includes('description')
  );
  const typeIndex = headers.findIndex(h => 
    h.toLowerCase() === 'type'
  );

  // Debug logging for indices
  console.log('Header indices:', {
    categoryNameIndex,
    kpiNameIndex,
    kpiCodeIndex,
    measurementTypeIndex,
    descriptionIndex,
    typeIndex
  });

  if (categoryNameIndex === -1 || kpiNameIndex === -1 || kpiCodeIndex === -1 || measurementTypeIndex === -1 || typeIndex === -1) {
    const missingHeaders = [];
    if (categoryNameIndex === -1) missingHeaders.push('Category Name');
    if (kpiNameIndex === -1) missingHeaders.push('KPI Name');
    if (kpiCodeIndex === -1) missingHeaders.push('KPI Code');
    if (measurementTypeIndex === -1) missingHeaders.push('Measurement Type');
    if (typeIndex === -1) missingHeaders.push('Type');
    
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}. Found headers: ${headers.join(', ')}`);
  }

  // Parse data rows
  const parsedRows: ParsedKPIRow[] = [];
  
  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const rowNumber = i + 2; // +2 for header and 0-based index
    
    // Skip empty rows
    if (!row || row.every(cell => !cell || cell.trim() === '')) {
      continue;
    }

    // Get type value and convert abbreviation to full type name if needed
    const typeValue = typeIndex >= 0 ? row[typeIndex]?.trim() : 'MTD';
    const fullType = getTypeFromAbbreviation(typeValue) || typeValue || 'Monthly Values';

    const parsedRow: ParsedKPIRow = {
      categoryName: row[categoryNameIndex]?.trim() || '',
      kpiName: row[kpiNameIndex]?.trim() || '',
      kpiCode: row[kpiCodeIndex]?.trim().toUpperCase() || '',
      measurementType: row[measurementTypeIndex]?.trim() || '',
      description: descriptionIndex >= 0 ? row[descriptionIndex]?.trim() : undefined,
      type: fullType,
      rowNumber
    };

    // Debug logging for first few rows
    if (i < 5) {
      console.log(`Row ${i + 1} parsed:`, {
        categoryName: parsedRow.categoryName,
        kpiName: parsedRow.kpiName,
        kpiCode: parsedRow.kpiCode,
        measurementType: parsedRow.measurementType,
        rawRow: row
      });
    }

    parsedRows.push(parsedRow);
  }

  // Debug logging for final results
  const uniqueCategories = new Set(parsedRows.map(row => row.categoryName));
  console.log('Parsing complete:', {
    totalRows: parsedRows.length,
    uniqueCategories: uniqueCategories.size,
    categoryNames: Array.from(uniqueCategories).slice(0, 10)
  });

  return parsedRows;
};
